import { Button } from '@mlm/ui';
import { Printer } from 'lucide-react';
import { PageHeader } from './_shared';
import { BILLING, BILLING_LINE_TOTALS, BILLING_TOTALS, VAT_PCT, peso } from './sample-data';

/**
 * Deliberately titled "Billing Statement", never "Sales Invoice" or "Official
 * Receipt" — those are BIR-registered statutory documents with their own
 * numbering, accreditation and format requirements, scoped separately.
 */
export function BillingStatementPage() {
  return (
    <div className="space-y-4">
      <div className="no-print">
        <PageHeader
          title="Billing Statement"
          subtitle="Issued against a completed order · VAT shown inclusive at the tenant rate"
          actions={
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="h-4 w-4" />
              Print
            </Button>
          }
        />
      </div>

      <div className="billing-doc rounded-md border bg-background p-6 text-foreground">
        <header className="flex items-start justify-between gap-6 border-b pb-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">BILLING STATEMENT</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{BILLING.issuedAt}</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-sm">{BILLING.docNo}</p>
            <p className="mt-0.5 text-xs uppercase text-muted-foreground">
              Order {BILLING.orderCode}
            </p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 border-b py-5">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Billed to
            </p>
            <p className="text-base font-semibold leading-tight">{BILLING.buyer.name}</p>
            <p className="text-sm leading-snug">{BILLING.buyer.address}</p>
            <p className="mt-1 text-sm">{BILLING.buyer.phone}</p>
            <p className="text-xs text-muted-foreground">TIN {BILLING.buyer.tin}</p>
          </div>
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Issued by
            </p>
            <p className="text-base font-semibold leading-tight">{BILLING.branch.name}</p>
            <p className="font-mono text-xs text-muted-foreground">{BILLING.branch.code}</p>
            <p className="mt-1 text-sm leading-snug">{BILLING.branch.address}</p>
            <p className="mt-2 text-xs">
              <span className="text-muted-foreground">Payment: </span>
              {BILLING.paymentMethod}
            </p>
          </div>
        </section>

        <section className="py-4">
          <p className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">Items</p>
          <table className="w-full text-sm">
            <thead className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2 pr-2">SKU</th>
                <th className="py-2 pr-2">Item</th>
                <th className="py-2 pr-2 text-right">Qty</th>
                <th className="py-2 pr-2 text-right">Unit price</th>
                <th className="py-2 pr-2 text-right">VATable</th>
                <th className="py-2 pr-2 text-right">VAT</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {BILLING_LINE_TOTALS.map((l) => (
                <tr key={l.sku} className="border-b align-top last:border-0">
                  <td className="whitespace-nowrap py-2 pr-2 font-mono text-xs">{l.sku}</td>
                  <td className="py-2 pr-2">{l.name}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{l.qty}</td>
                  <td className="py-2 pr-2 text-right font-mono tabular-nums">
                    {peso(l.unitPrice)}
                  </td>
                  <td className="py-2 pr-2 text-right font-mono tabular-nums text-muted-foreground">
                    {peso(l.vatable)}
                  </td>
                  <td className="py-2 pr-2 text-right font-mono tabular-nums text-muted-foreground">
                    {peso(l.vat)}
                  </td>
                  <td className="py-2 text-right font-mono text-base font-semibold tabular-nums">
                    {peso(l.lineTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="flex justify-end border-t pt-4">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">VATable sales</span>
              <span className="font-mono tabular-nums">{peso(BILLING_TOTALS.vatable)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">VAT ({VAT_PCT}%)</span>
              <span className="font-mono tabular-nums">{peso(BILLING_TOTALS.vat)}</span>
            </div>
            <div className="flex justify-between border-t pt-1">
              <span className="text-muted-foreground">Goods total (VAT inclusive)</span>
              <span className="font-mono tabular-nums">{peso(BILLING_TOTALS.goods)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shipping</span>
              <span className="font-mono tabular-nums">{peso(BILLING_TOTALS.shipping)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 text-base font-semibold">
              <span>Total amount due</span>
              <span className="font-mono tabular-nums">{peso(BILLING_TOTALS.total)}</span>
            </div>
          </div>
        </section>

        <footer className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
          <span>
            Prices are VAT inclusive. This billing statement is not a BIR-registered Sales Invoice
            or Official Receipt.
          </span>
          <span className="font-mono">{BILLING.docNo}</span>
        </footer>
      </div>
    </div>
  );
}
